import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import { 
  Card, 
  Table,
  Form, 
  Input, 
  Select, 
  Button, 
  Typography, 
  notification,
  Breadcrumb,
  Space,
  Alert,
  Tag,
  Modal,
  Badge,
  Popconfirm
} from 'antd';
import { 
  FaTools, 
  FaHome, 
  FaExclamationTriangle,
  FaInfoCircle,
  FaArrowLeft,
  FaPlus,
  FaSyncAlt,
  FaEye,
  FaEdit,
  FaTrash
} from 'react-icons/fa';
import './MaintenanceRequestForm.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function MaintenanceRequestPage() {
  const navigate = useNavigate();
  const { authData } = useAuth();
  const [form] = Form.useForm();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const breadcrumbItems = [
    {
      title: <a onClick={() => navigate(-1)}>Back</a>,
    },
    {
      title: 'Maintenance Requests',
    },
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/Maintenance/GetAllMaintenanceTasks', {
        params: { hotelId: authData.hotelId }
      });
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      setError(error.response?.data?.message || 'Failed to fetch requests');
      notification.error({
        message: 'Error',
        description: 'Failed to fetch maintenance requests'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomIdByRoomNumber = async (roomNumber) => {
    if (!roomNumber) return null;
    try {
      const response = await axiosInstance.get('/Rooms/GetByNumber', {
        params: { roomNumber, hotelId: authData.hotelId }
      });
      return response.data.roomId;
    } catch (error) {
      console.error('Error fetching room by number:', error);
      return null;
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const resolvedRoomId = await fetchRoomIdByRoomNumber(values.roomNumber);

      const requestBody = {
        hotelId: authData.hotelId,
        reportedBy: authData.userId,
        roomNumber: values.roomNumber,
        roomId: resolvedRoomId,
        location: values.location,
        requestedByUserId: authData.userId,
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status || 'Pending'
      };

      if (selectedRequest) {
        // Update existing request
        await axiosInstance.put(`/Maintenance/${selectedRequest.maintenanceTaskId}`, requestBody, {
          params: { hotelId: authData.hotelId }
        });
        notification.success({
          message: 'Success',
          description: 'Request updated successfully'
        });
      } else {
        // Create new request
        await axiosInstance.post('/Maintenance/CreateTask', requestBody);
        notification.success({
          message: 'Success',
          description: 'Request submitted successfully'
        });
      }
      
      setModalOpen(false);
      fetchRequests();
    } catch (error) {
      console.error('Failed to submit request:', error);
      setError(error.response?.data?.message || 'Failed to submit request');
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to submit request'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (requestId) => {
    try {
      await axiosInstance.delete(`/Maintenance/${requestId}`, {
        params: { hotelId: authData.hotelId }
      });
      notification.success({
        message: 'Success',
        description: 'Request deleted successfully'
      });
      fetchRequests();
    } catch (error) {
      console.error('Failed to delete request:', error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to delete request'
      });
    }
  };

  const priorityOptions = [
    { value: 'Low', label: 'Low', color: 'green' },
    { value: 'Medium', label: 'Medium', color: 'orange' },
    { value: 'High', label: 'High', color: 'red' },
    { value: 'Emergency', label: 'Emergency', color: 'volcano' }
  ];

  const statusOptions = [
    { value: 'Pending', label: 'Pending', color: 'orange' },
    { value: 'InProgress', label: 'In Progress', color: 'blue' },
    { value: 'Completed', label: 'Completed', color: 'green' },
    { value: 'Cancelled', label: 'Cancelled', color: 'red' }
  ];

  const locationOptions = [
    'General',
    'Lobby',
    'Kitchen',
    'Restaurant',
    'Pool',
    'Gym',
    'Restroom',
    'Hallway',
    'Elevator',
    'Parking'
  ];

  const columns = [
    {
      title: 'Request ID',
      dataIndex: 'maintenanceTaskId',
      key: 'maintenanceTaskId',
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Room',
      dataIndex: 'roomNumber',
      key: 'roomNumber',
      render: (text) => text || 'N/A'
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const option = priorityOptions.find(opt => opt.value === priority);
        return <Tag color={option?.color}>{option?.label}</Tag>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const option = statusOptions.find(opt => opt.value === status);
        return <Tag color={option?.color}>{option?.label}</Tag>;
      }
    },
    {
      title: 'Requested',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleString() : 'N/A'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<FaEye />} 
            onClick={() => {
              setSelectedRequest(record);
              setModalOpen(true);
            }}
          />
          <Button 
            icon={<FaEdit />} 
            onClick={() => {
              form.setFieldsValue({
                ...record,
                roomNumber: record.roomNumber || ''
              });
              setSelectedRequest(record);
              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this request?"
            onConfirm={() => handleDelete(record.maintenanceTaskId)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<FaTrash />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="maintenance-request-page">
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />
      
      <Card
        title={
          <Space>
            <FaTools style={{ fontSize: '18px', marginRight: '8px' }} />
            <Title level={4} style={{ margin: 0 }}>Maintenance Requests</Title>
            <Badge count={requests.length} showZero style={{ backgroundColor: '#1890ff' }} />
          </Space>
        }
        extra={
          <Space>
            <Button 
              icon={<FaSyncAlt />} 
              onClick={fetchRequests}
              loading={loading}
            >
              Refresh
            </Button>
            <Button 
              type="primary" 
              icon={<FaPlus />}
              onClick={() => {
                form.resetFields();
                setSelectedRequest(null);
                setModalOpen(true);
              }}
            >
              New Request
            </Button>
          </Space>
        }
      >
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 24 }}
            onClose={() => setError(null)}
          />
        )}

        <Table
          columns={columns}
          dataSource={requests}
          rowKey="maintenanceTaskId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50']
          }}
          scroll={{ x: true }}
        />

        <Modal
          title={selectedRequest ? 'Edit Maintenance Request' : 'New Maintenance Request'}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          width={700}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              location: 'General',
              priority: 'Medium',
              status: 'Pending'
            }}
          >
            <Form.Item
              label="Room Number (optional)"
              name="roomNumber"
            >
              <Input 
                placeholder="e.g. 101" 
                prefix={<FaHome />}
              />
            </Form.Item>

            <Form.Item
              label="Location"
              name="location"
              rules={[{ required: true, message: 'Please select a location' }]}
            >
              <Select placeholder="Select location">
                {locationOptions.map(loc => (
                  <Option key={loc} value={loc}>{loc}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Priority"
              name="priority"
              rules={[{ required: true, message: 'Please select priority' }]}
            >
              <Select placeholder="Select priority">
                {priorityOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>
                    <Tag color={opt.color}>{opt.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select placeholder="Select status">
                {statusOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>
                    <Tag color={opt.color}>{opt.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input 
                placeholder="Brief description of issue" 
                maxLength={100}
              />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: 'Please enter a description' }]}
            >
              <TextArea 
                rows={4} 
                placeholder="Detailed description of the problem"
                showCount 
                maxLength={500}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={submitting}
                  icon={<FaExclamationTriangle />}
                >
                  {selectedRequest ? 'Update Request' : 'Submit Request'}
                </Button>
                <Button 
                  onClick={() => setModalOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
}

export default MaintenanceRequestPage;